/**
 * Demandes de test de l'app Android (migration 0732) : adresses Google laissées
 * sur le site (bouton Google Play). L'admin les ajoute aux testeurs dans la
 * Play Console, puis envoie l'invitation (edge function `android-tester-invite` :
 * lien « Devenir testeur » + lien de l'app).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface AndroidTesterRequest {
  id: string;
  email: string;
  source: string;
  request_count: number;
  created_at: string;
  last_request_at: string;
  invited_at: string | null;
  invite_count: number;
}

export type TesterFilter = 'pending' | 'invited' | 'all';

/** Filtre pur (testé) : à inviter, déjà invitées, ou tout. */
export function filterTesters(
  rows: AndroidTesterRequest[],
  filter: TesterFilter,
): AndroidTesterRequest[] {
  if (filter === 'pending') return rows.filter((r) => !r.invited_at);
  if (filter === 'invited') return rows.filter((r) => !!r.invited_at);
  return rows;
}

/**
 * Les adresses à coller dans la Play Console (liste de testeurs : séparées par
 * des virgules), sans doublon.
 */
export function testerEmailList(rows: Pick<AndroidTesterRequest, 'email'>[]): string {
  return [...new Set(rows.map((r) => r.email.trim().toLowerCase()))].join(', ');
}

/** Message lisible d'une erreur d'envoi de l'invitation. */
export function inviteErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'email_unconfigured':
      return 'L’envoi d’emails n’est pas configuré sur le serveur.';
    case 'email_failed':
      return 'L’email n’est pas parti. Réessaie dans un instant.';
    case 'not_found':
      return 'Cette demande n’existe plus.';
    case 'forbidden':
      return 'Réservé aux admins.';
    default:
      return 'L’invitation n’a pas pu être envoyée.';
  }
}

const QUERY_KEY = ['android-tester-requests'] as const;

/** Admin : toutes les demandes (500 plus récentes). */
export function useAndroidTesterRequests(enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEY,
    enabled,
    queryFn: async (): Promise<AndroidTesterRequest[]> => {
      // Table postérieure aux types générés : client générique.
      const db = supabase as unknown as SupabaseClient;
      const { data, error } = await db
        .from('android_tester_requests')
        .select(
          'id, email, source, request_count, created_at, last_request_at, invited_at, invite_count',
        )
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw new Error('Impossible de charger les demandes.');
      return (data ?? []) as AndroidTesterRequest[];
    },
  });
}

/** Admin : envoyer (ou renvoyer) l'invitation par email. */
export function useSendAndroidTesterInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
        'android-tester-invite',
        { body: { request_id: requestId } },
      );
      if (error || !data?.ok) {
        let code = data?.error;
        const ctx = (error as { context?: Response } | null)?.context;
        if (!code && ctx && typeof ctx.json === 'function') {
          try {
            code = ((await ctx.json()) as { error?: string }).error;
          } catch {
            /* corps illisible */
          }
        }
        throw new Error(inviteErrorMessage(code));
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

/** Admin : retirer une demande (doublon, adresse fausse). */
export function useDeleteAndroidTesterRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const db = supabase as unknown as SupabaseClient;
      const { error } = await db.from('android_tester_requests').delete().eq('id', requestId);
      if (error) throw new Error('La suppression a échoué.');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
